/**
 * MetaTargetingPanel
 * Platform-specific targeting UI for Meta (Facebook/Instagram)
 *
 * Redesigned to align with Meta's Advantage+ recommendations:
 * - Advantage+ Audience as default (AI-optimized)
 * - Simplified demographics/locations
 * - Detailed targeting (interests/behaviors) in advanced section
 */

import React, { useState, useEffect } from 'react';
import {
    Users,
    Heart,
    ShoppingBag,
    MapPin,
    ChevronDown,
    ChevronRight,
    Loader2,
    Sparkles,
    Target,
    UserPlus,
    Info,
    Zap
} from 'lucide-react';
import InterestSearch from './InterestSearch';
import LocationSearch from './LocationSearch';
import AudienceEstimate from './AudienceEstimate';
import AITargetingHelper from './AITargetingHelper';

const AUDIENCE_STRATEGIES = {
    advantage_plus: {
        id: 'advantage_plus',
        name: 'Advantage+ Audience',
        description: "Meta's AI finds your best customers",
        icon: Zap,
        recommended: true,
        benefit: 'Typically 15-30% lower cost per result'
    },
    custom: {
        id: 'custom',
        name: 'Custom Audiences',
        description: 'Website visitors, customer lists, app users',
        icon: Target,
        recommended: false
    },
    lookalike: {
        id: 'lookalike',
        name: 'Lookalike Audiences',
        description: 'People similar to your best customers',
        icon: UserPlus,
        recommended: false
    }
};

export default function MetaTargetingPanel({
    brandId,
    userId,
    brand,
    targeting = {},
    onChange,
    isConnected,
    accountId = null,
    funnelStage = 'TOFU'
}) {
    const [customAudiences, setCustomAudiences] = useState([]);
    const [savedAudiences, setSavedAudiences] = useState([]);
    const [loadingAudiences, setLoadingAudiences] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [expandedAdvanced, setExpandedAdvanced] = useState({
        interests: false,
        behaviors: false
    });

    // Current audience strategy (default to advantage_plus)
    const audienceStrategy = targeting.audienceStrategy || 'advantage_plus';

    useEffect(() => {
        if (isConnected && accountId) {
            loadAudiences();
        }
    }, [isConnected, accountId]);

    const loadAudiences = async () => {
        setLoadingAudiences(true);
        try {
            const response = await fetch(`/api/targeting/meta/audiences?brandId=${brandId}&userId=${userId}`);
            if (response.ok) {
                const data = await response.json();
                setCustomAudiences(data.custom || []);
                setSavedAudiences(data.saved || []);
            }
        } catch (err) {
            console.error('Error loading audiences:', err);
        } finally {
            setLoadingAudiences(false);
        }
    };

    const updateTargeting = (key, value) => {
        onChange({ ...targeting, [key]: value });
    };

    const updateDemographics = (field, value) => {
        onChange({
            ...targeting,
            demographics: { ...targeting.demographics, [field]: value }
        });
    };

    const handleStrategyChange = (strategyId) => {
        updateTargeting('audienceStrategy', strategyId);
    };

    const handleInterestSelect = (itemOrItems) => {
        const current = targeting.detailedTargeting?.meta?.interests || [];
        const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            meta: { ...targeting.detailedTargeting?.meta, interests: [...current, ...items] }
        });
    };

    const handleInterestRemove = (item) => {
        const current = targeting.detailedTargeting?.meta?.interests || [];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            meta: { ...targeting.detailedTargeting?.meta, interests: current.filter(i => i.id !== item.id) }
        });
    };

    const handleBehaviorSelect = (itemOrItems) => {
        const current = targeting.detailedTargeting?.meta?.behaviors || [];
        const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            meta: { ...targeting.detailedTargeting?.meta, behaviors: [...current, ...items] }
        });
    };

    const handleBehaviorRemove = (item) => {
        const current = targeting.detailedTargeting?.meta?.behaviors || [];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            meta: { ...targeting.detailedTargeting?.meta, behaviors: current.filter(b => b.id !== item.id) }
        });
    };

    const handleLocationSelect = (loc) => {
        const current = targeting.locations?.included || [];
        updateTargeting('locations', { ...targeting.locations, included: [...current, loc] });
    };

    const handleLocationRemove = (loc) => {
        const current = targeting.locations?.included || [];
        updateTargeting('locations', {
            ...targeting.locations,
            included: current.filter(l => (l.key || l.id) !== (loc.key || loc.id))
        });
    };

    const handleAudienceToggle = (audience, type) => {
        const current = targeting.audiences?.included || [];
        const isSelected = current.some(a => a.id === audience.id);

        if (isSelected) {
            updateTargeting('audiences', {
                ...targeting.audiences,
                included: current.filter(a => a.id !== audience.id)
            });
        } else {
            updateTargeting('audiences', {
                ...targeting.audiences,
                included: [...current, { ...audience, type }]
            });
        }
    };

    const toggleAdvancedSection = (section) => {
        setExpandedAdvanced(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const interestCount = targeting.detailedTargeting?.meta?.interests?.length || 0;
    const behaviorCount = targeting.detailedTargeting?.meta?.behaviors?.length || 0;
    const hasDetailedTargeting = interestCount > 0 || behaviorCount > 0;

    return (
        <div className="meta-targeting-panel">
            {/* Audience Strategy Section */}
            <div className="strategy-section">
                <div className="section-label">
                    <Sparkles size={14} />
                    <span>Audience Strategy</span>
                </div>

                <div className="strategy-options">
                    {Object.values(AUDIENCE_STRATEGIES).map(strategy => {
                        const Icon = strategy.icon;
                        const isSelected = audienceStrategy === strategy.id;

                        return (
                            <label
                                key={strategy.id}
                                className={`strategy-option ${isSelected ? 'selected' : ''} ${strategy.recommended ? 'recommended' : ''}`}
                            >
                                <input
                                    type="radio"
                                    name="audienceStrategy"
                                    value={strategy.id}
                                    checked={isSelected}
                                    onChange={() => handleStrategyChange(strategy.id)}
                                />
                                <div className="strategy-content">
                                    <div className="strategy-header">
                                        <Icon size={16} />
                                        <span className="strategy-name">{strategy.name}</span>
                                        {strategy.recommended && (
                                            <span className="recommended-badge">Recommended</span>
                                        )}
                                    </div>
                                    <p className="strategy-desc">{strategy.description}</p>
                                    {strategy.benefit && isSelected && (
                                        <p className="strategy-benefit">
                                            <Info size={12} />
                                            {strategy.benefit}
                                        </p>
                                    )}
                                </div>
                            </label>
                        );
                    })}
                </div>

                {/* Custom/Lookalike Audience Selector */}
                {(audienceStrategy === 'custom' || audienceStrategy === 'lookalike') && (
                    <div className="audience-selector">
                        {loadingAudiences ? (
                            <div className="loading-audiences">
                                <Loader2 size={16} className="spinning" />
                                <span>Loading audiences...</span>
                            </div>
                        ) : customAudiences.length === 0 && savedAudiences.length === 0 ? (
                            <div className="no-audiences">
                                <Users size={20} />
                                <p>No audiences available</p>
                                <span>Create audiences in Meta Ads Manager first</span>
                            </div>
                        ) : (
                            <div className="audience-list">
                                {customAudiences
                                    .filter(a => audienceStrategy === 'lookalike' ? a.isLookalike : !a.isLookalike)
                                    .map(audience => (
                                        <label key={audience.id} className="audience-item">
                                            <input
                                                type="checkbox"
                                                checked={targeting.audiences?.included?.some(a => a.id === audience.id)}
                                                onChange={() => handleAudienceToggle(audience, 'custom')}
                                            />
                                            <div className="audience-info">
                                                <span className="audience-name">{audience.name}</span>
                                                <span className="audience-size">
                                                    {audience.approximateCount?.toLocaleString() || '—'} people
                                                </span>
                                            </div>
                                        </label>
                                    ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Demographics & Locations Section - Always Visible */}
            <div className="demographics-section">
                <div className="section-label">
                    <MapPin size={14} />
                    <span>Demographics & Locations</span>
                </div>

                <div className="demographics-grid">
                    {/* Age Range */}
                    <div className="form-group">
                        <label>Age</label>
                        <div className="age-range">
                            <select
                                value={targeting.demographics?.ageMin || 18}
                                onChange={(e) => updateDemographics('ageMin', parseInt(e.target.value))}
                            >
                                {[18, 21, 25, 30, 35, 40, 45, 50, 55, 60, 65].map(age => (
                                    <option key={age} value={age}>{age}</option>
                                ))}
                            </select>
                            <span>to</span>
                            <select
                                value={targeting.demographics?.ageMax || 65}
                                onChange={(e) => updateDemographics('ageMax', parseInt(e.target.value))}
                            >
                                {[24, 30, 35, 40, 45, 50, 55, 60, 65].map(age => (
                                    <option key={age} value={age}>{age === 65 ? '65+' : age}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Gender */}
                    <div className="form-group">
                        <label>Gender</label>
                        <div className="gender-options">
                            {['all', 'male', 'female'].map(gender => (
                                <button
                                    key={gender}
                                    type="button"
                                    className={`gender-btn ${(targeting.demographics?.genders || ['all']).includes(gender) ? 'active' : ''}`}
                                    onClick={() => {
                                        if (gender === 'all') {
                                            updateDemographics('genders', ['all']);
                                        } else {
                                            const current = targeting.demographics?.genders || [];
                                            const filtered = current.filter(g => g !== 'all');
                                            if (filtered.includes(gender)) {
                                                updateDemographics('genders', filtered.filter(g => g !== gender) || ['all']);
                                            } else {
                                                updateDemographics('genders', [...filtered, gender]);
                                            }
                                        }
                                    }}
                                >
                                    {gender === 'all' ? 'All' : gender.charAt(0).toUpperCase() + gender.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Locations */}
                <div className="locations-group">
                    <label>Locations</label>
                    <LocationSearch
                        platform="meta"
                        brandId={brandId}
                        userId={userId}
                        selectedLocations={targeting.locations?.included || []}
                        excludedLocations={targeting.locations?.excluded || []}
                        onSelect={handleLocationSelect}
                        onRemove={handleLocationRemove}
                        showExclusions={false}
                        placeholder="Search countries, regions, cities..."
                    />
                </div>
            </div>

            {/* Advanced Targeting Section - Collapsible */}
            <div className="advanced-section">
                <button
                    type="button"
                    className={`advanced-toggle ${showAdvanced ? 'expanded' : ''}`}
                    onClick={() => setShowAdvanced(!showAdvanced)}
                >
                    {showAdvanced ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <span>Advanced: Detailed Targeting</span>
                    {hasDetailedTargeting && (
                        <span className="detail-count">{interestCount + behaviorCount} selected</span>
                    )}
                    <span className="optional-tag">Optional</span>
                </button>

                {showAdvanced && (
                    <div className="advanced-content">
                        <p className="advanced-note">
                            <Info size={14} />
                            With Advantage+, these serve as suggestions. Meta's AI may expand beyond them for better results.
                        </p>

                        {/* Interests */}
                        <div className="advanced-group">
                            <button
                                type="button"
                                className="group-header"
                                onClick={() => toggleAdvancedSection('interests')}
                            >
                                <Heart size={14} />
                                <span>Interests</span>
                                {interestCount > 0 && <span className="count">{interestCount}</span>}
                                {expandedAdvanced.interests ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>

                            {expandedAdvanced.interests && (
                                <div className="group-content">
                                    <AITargetingHelper
                                        platform="meta"
                                        type="interest"
                                        brandId={brandId}
                                        userId={userId}
                                        brand={brand}
                                        selectedItems={targeting.detailedTargeting?.meta?.interests || []}
                                        onSelect={handleInterestSelect}
                                        funnelStage={funnelStage}
                                        disabled={!isConnected}
                                    />
                                    <InterestSearch
                                        platform="meta"
                                        type="interest"
                                        brandId={brandId}
                                        userId={userId}
                                        selectedItems={targeting.detailedTargeting?.meta?.interests || []}
                                        onSelect={handleInterestSelect}
                                        onRemove={handleInterestRemove}
                                        placeholder="Search interests..."
                                    />
                                </div>
                            )}
                        </div>

                        {/* Behaviors */}
                        <div className="advanced-group">
                            <button
                                type="button"
                                className="group-header"
                                onClick={() => toggleAdvancedSection('behaviors')}
                            >
                                <ShoppingBag size={14} />
                                <span>Behaviors</span>
                                {behaviorCount > 0 && <span className="count">{behaviorCount}</span>}
                                {expandedAdvanced.behaviors ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>

                            {expandedAdvanced.behaviors && (
                                <div className="group-content">
                                    <InterestSearch
                                        platform="meta"
                                        type="behavior"
                                        brandId={brandId}
                                        userId={userId}
                                        selectedItems={targeting.detailedTargeting?.meta?.behaviors || []}
                                        onSelect={handleBehaviorSelect}
                                        onRemove={handleBehaviorRemove}
                                        placeholder="Browse behaviors..."
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Audience Estimate */}
            <div className="estimate-section">
                <AudienceEstimate
                    platform="meta"
                    brandId={brandId}
                    userId={userId}
                    targeting={targeting}
                />
            </div>

            <style>{`
                .meta-targeting-panel {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .section-label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: var(--color-text-tertiary);
                    margin-bottom: 0.75rem;
                }

                /* Strategy Section */
                .strategy-section {
                    background: var(--color-surface);
                    border: 1px solid var(--color-border);
                    border-radius: 8px;
                    padding: 1rem;
                }

                .strategy-options {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .strategy-option {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                    padding: 0.875rem;
                    background: var(--color-bg-primary);
                    border: 2px solid var(--color-border);
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.15s;
                }

                .strategy-option:hover {
                    border-color: var(--color-primary-light);
                }

                .strategy-option.selected {
                    border-color: var(--color-primary);
                    background: var(--color-primary-light);
                }

                .strategy-option.recommended {
                    position: relative;
                }

                .strategy-option input[type="radio"] {
                    margin-top: 2px;
                    accent-color: var(--color-primary);
                }

                .strategy-content {
                    flex: 1;
                }

                .strategy-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 0.25rem;
                }

                .strategy-name {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: var(--color-text-primary);
                }

                .recommended-badge {
                    padding: 0.125rem 0.5rem;
                    background: var(--color-success);
                    color: white;
                    border-radius: 4px;
                    font-size: 0.625rem;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .strategy-desc {
                    margin: 0;
                    font-size: 0.8125rem;
                    color: var(--color-text-secondary);
                }

                .strategy-benefit {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    margin: 0.5rem 0 0;
                    padding: 0.375rem 0.5rem;
                    background: var(--color-success-light);
                    border-radius: 4px;
                    font-size: 0.75rem;
                    color: var(--color-success-dark);
                }

                /* Audience Selector */
                .audience-selector {
                    margin-top: 0.75rem;
                    padding-top: 0.75rem;
                    border-top: 1px solid var(--color-border);
                }

                .loading-audiences,
                .no-audiences {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 1.5rem;
                    color: var(--color-text-tertiary);
                    text-align: center;
                }

                .no-audiences p {
                    margin: 0;
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: var(--color-text-secondary);
                }

                .no-audiences span {
                    font-size: 0.75rem;
                }

                .spinning {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .audience-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.375rem;
                    max-height: 200px;
                    overflow-y: auto;
                }

                .audience-item {
                    display: flex;
                    align-items: center;
                    gap: 0.625rem;
                    padding: 0.5rem 0.625rem;
                    background: var(--color-bg-secondary);
                    border-radius: 6px;
                    cursor: pointer;
                    transition: background 0.15s;
                }

                .audience-item:hover {
                    background: var(--color-bg-tertiary);
                }

                .audience-item input[type="checkbox"] {
                    width: 16px;
                    height: 16px;
                    accent-color: var(--color-primary);
                }

                .audience-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }

                .audience-name {
                    font-size: 0.8125rem;
                    color: var(--color-text-primary);
                }

                .audience-size {
                    font-size: 0.6875rem;
                    color: var(--color-text-tertiary);
                }

                /* Demographics Section */
                .demographics-section {
                    background: var(--color-surface);
                    border: 1px solid var(--color-border);
                    border-radius: 8px;
                    padding: 1rem;
                }

                .demographics-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                    margin-bottom: 1rem;
                }

                .form-group label {
                    display: block;
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: var(--color-text-tertiary);
                    margin-bottom: 0.375rem;
                }

                .age-range {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .age-range select {
                    flex: 1;
                    padding: 0.5rem;
                    background: var(--color-bg-primary);
                    border: 1px solid var(--color-border);
                    border-radius: 4px;
                    font-size: 0.8125rem;
                    color: var(--color-text-primary);
                }

                .age-range span {
                    color: var(--color-text-tertiary);
                    font-size: 0.8125rem;
                }

                .gender-options {
                    display: flex;
                    gap: 0.375rem;
                }

                .gender-btn {
                    flex: 1;
                    padding: 0.5rem;
                    background: var(--color-bg-primary);
                    border: 1px solid var(--color-border);
                    border-radius: 4px;
                    font-size: 0.8125rem;
                    color: var(--color-text-tertiary);
                    cursor: pointer;
                    transition: all 0.15s;
                }

                .gender-btn.active {
                    background: var(--color-primary);
                    border-color: var(--color-primary);
                    color: white;
                }

                .locations-group label {
                    display: block;
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: var(--color-text-tertiary);
                    margin-bottom: 0.375rem;
                }

                /* Advanced Section */
                .advanced-section {
                    background: var(--color-surface);
                    border: 1px solid var(--color-border);
                    border-radius: 8px;
                    overflow: hidden;
                }

                .advanced-toggle {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    width: 100%;
                    padding: 0.75rem 1rem;
                    background: var(--color-bg-primary);
                    border: none;
                    text-align: left;
                    cursor: pointer;
                    color: var(--color-text-secondary);
                    font-size: 0.8125rem;
                    transition: all 0.15s;
                }

                .advanced-toggle:hover {
                    background: var(--color-bg-secondary);
                }

                .advanced-toggle.expanded {
                    border-bottom: 1px solid var(--color-border);
                }

                .advanced-toggle span:first-of-type {
                    flex: 1;
                }

                .detail-count {
                    padding: 0.125rem 0.5rem;
                    background: var(--color-primary);
                    color: white;
                    border-radius: 10px;
                    font-size: 0.6875rem;
                    font-weight: 500;
                }

                .optional-tag {
                    padding: 0.125rem 0.375rem;
                    background: var(--color-bg-tertiary);
                    color: var(--color-text-tertiary);
                    border-radius: 3px;
                    font-size: 0.625rem;
                    text-transform: uppercase;
                }

                .advanced-content {
                    padding: 1rem;
                }

                .advanced-note {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.5rem;
                    margin: 0 0 1rem;
                    padding: 0.625rem 0.75rem;
                    background: var(--color-info-light);
                    border-radius: 6px;
                    font-size: 0.75rem;
                    color: var(--color-info-dark);
                    line-height: 1.4;
                }

                .advanced-note svg {
                    flex-shrink: 0;
                    margin-top: 1px;
                }

                .advanced-group {
                    border: 1px solid var(--color-border);
                    border-radius: 6px;
                    margin-bottom: 0.5rem;
                }

                .advanced-group:last-child {
                    margin-bottom: 0;
                }

                .group-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    width: 100%;
                    padding: 0.625rem 0.75rem;
                    background: var(--color-bg-primary);
                    border: none;
                    text-align: left;
                    cursor: pointer;
                    font-size: 0.8125rem;
                    color: var(--color-text-primary);
                }

                .group-header:hover {
                    background: var(--color-bg-secondary);
                }

                .group-header span:first-of-type {
                    flex: 1;
                }

                .group-header .count {
                    padding: 0.125rem 0.375rem;
                    background: var(--color-primary-light);
                    color: var(--color-primary);
                    border-radius: 10px;
                    font-size: 0.625rem;
                    font-weight: 600;
                }

                .group-content {
                    padding: 0.75rem;
                    border-top: 1px solid var(--color-border);
                }

                /* Estimate Section */
                .estimate-section {
                    margin-top: 0.5rem;
                }
            `}</style>
        </div>
    );
}
